import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import Feather from '@expo/vector-icons/Feather';
import { RootState, AppDispatch } from '../../src/redux/store';
import {
  selectTasteAxes,
  selectAllergens,
  selectCustomAllergens,
  setAllergens,
  flipTasteAxis,
} from '../../src/redux/userPreferencesSlice';
import { recordAxisFlip } from '../../src/redux/onboardingSlice';
import { AXES, type Axis, type TasteAxes } from '../../src/services/tasteAxes';
import { ALLERGEN_KEYS, type AllergenKey } from '../../src/data/allergens';
import { AxisRow } from '../../src/components/onboarding/AxisRow';
import { AllergenChip } from '../../src/components/onboarding/AllergenChip';
import {
  selectTasteProfile,
  selectFeedbackCount,
  TasteProfileChangeLogEntry,
} from '../../src/redux/feedbackSlice';
import { digestFeedback } from '../../src/services/tasteProfileService';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../src/styles/theme';
import { parseDayOrInstant } from '../../src/utils/localDate';

// ---- Helpers ----

const formatPreferenceKey = (key: string): string =>
  key
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (iso: string, locale: string = 'en-US'): string => {
  const d = parseDayOrInstant(iso);
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatShortDate = (iso: string, locale: string = 'en-US'): string => {
  const d = parseDayOrInstant(iso);
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};

/** The other reading of each axis, for the pill on the right of the row. */
const OPPOSITE: { [A in Axis]: Record<TasteAxes[A], TasteAxes[A]> } = {
  base: { protein: 'vegetal', vegetal: 'protein' },
  time: { quick: 'long', long: 'quick' },
  heat: { bold: 'mild', mild: 'bold' },
};

/** Literal keys so the orphan-key guard sees every one of them. */
const CHIP_LABEL: { [A in Axis]: Record<TasteAxes[A], string> } = {
  base: { protein: 'onboarding.reading.chip.base.protein', vegetal: 'onboarding.reading.chip.base.vegetal' },
  time: { quick: 'onboarding.reading.chip.time.quick', long: 'onboarding.reading.chip.time.long' },
  heat: { bold: 'onboarding.reading.chip.heat.bold', mild: 'onboarding.reading.chip.heat.mild' },
};
const AXIS_NAME: Record<Axis, string> = {
  base: 'onboarding.profile.axis.base',
  time: 'onboarding.profile.axis.time',
  heat: 'onboarding.profile.axis.heat',
};
const ALLERGEN_LABEL: Record<AllergenKey, string> = {
  arachides: 'onboarding.safety.allergen.arachides',
  noix: 'onboarding.safety.allergen.noix',
  crustaces: 'onboarding.safety.allergen.crustaces',
  lactose: 'onboarding.safety.allergen.lactose',
  gluten: 'onboarding.safety.allergen.gluten',
  oeufs: 'onboarding.safety.allergen.oeufs',
  soja: 'onboarding.safety.allergen.soja',
  poisson: 'onboarding.safety.allergen.poisson',
  sesame: 'onboarding.safety.allergen.sesame',
};

// ---- Component ----

const TasteProfileScreen: React.FC = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector(selectTasteProfile);
  const feedbackCount = useSelector(selectFeedbackCount);
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const axes = useSelector(selectTasteAxes);
  const allergens = useSelector(selectAllergens);
  const customAllergens = useSelector(selectCustomAllergens);
  const dietaryRestrictions = useSelector((state: RootState) => state.userPreferences?.dietaryRestrictions ?? []);
  const dislikedIngredients = useSelector((state: RootState) => state.userPreferences?.dislikedIngredients ?? []);

  const [refreshing, setRefreshing] = useState(false);
  const [changelogExpanded, setChangelogExpanded] = useState(false);
  const [axisSaved, setAxisSaved] = useState(false);
  // Same breakpoint as the onboarding profile screen: below it the axis name
  // sits above its value instead of beside it.
  const compact = useWindowDimensions().width < 700;

  /**
   * Flipping an axis from Réglages records the correction and stops there —
   * the next generation reads it. The onboarding read-back rebuilds the week
   * on the spot because that week is Du Chef's proposal and nothing is lost;
   * here the week on screen may be hand-picked, and silently replacing it
   * would cost the cook work they did themselves.
   */
  const handleFlip = useCallback((axis: Axis) => {
    dispatch(flipTasteAxis(axis));
    dispatch(recordAxisFlip({ axis }));
    setAxisSaved(true);
  }, [dispatch]);

  const toggleAllergen = useCallback((key: AllergenKey) => {
    const next = allergens.includes(key)
      ? allergens.filter((k) => k !== key)
      : [...allergens, key];
    dispatch(setAllergens({ keys: next, custom: customAllergens }));
  }, [allergens, customAllergens, dispatch]);

  const getState = useCallback(() => {
    // This is a workaround — we need the store's getState.
    // In practice, digestFeedback gets it from the middleware.
    // For manual trigger, we pass a minimal version.
    return undefined as any;
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!userId) return;
    setRefreshing(true);
    try {
      // Import store dynamically to get getState
      const { store } = require('../../src/redux/store');
      await digestFeedback(dispatch, store.getState, userId);
    } catch (error) {
      console.warn('Manual digest failed:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, userId]);

  const isEmptyState = profile === null && feedbackCount === 0;
  const isPendingDigest = profile === null && feedbackCount > 0;

  const visibleChangelog =
    profile && !changelogExpanded && profile.changeLog.length > 3
      ? profile.changeLog.slice(0, 3)
      : profile?.changeLog ?? [];

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          accessibilityLabel={t('navigation.back')}
        >
          <Feather name="arrow-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{t('tasteProfile.title')}</Text>
          {profile && (
            <Text style={styles.headerSubtitle}>
              {t('tasteProfile.signals', { count: profile.feedbackCount })} · {t('tasteProfile.updated', { date: formatDate(profile.lastUpdated, locale) })}
            </Text>
          )}
        </View>
        <View style={styles.headerSpacer} />
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={Paprika.accent}
            colors={[Paprika.accent]}
          />
        }
      >
        {/* What the cook chose — the three axes, still correctable. */}
        {axes && (
          <View style={styles.block} testID="taste-axes">
            <Text style={styles.blockTitle}>{t('tasteProfile.chosenTitle')}</Text>
            <View style={styles.axisList}>
              {AXES.map((axis) => (
                <AxisRow
                  key={axis}
                  testID={`taste-axis-${axis}`}
                  name={t(AXIS_NAME[axis])}
                  current={t(CHIP_LABEL[axis][axes[axis] as never])}
                  opposite={t(CHIP_LABEL[axis][OPPOSITE[axis][axes[axis] as never] as never])}
                  onFlip={() => handleFlip(axis)}
                  compact={compact}
                />
              ))}
            </View>
            <Text style={styles.blockNote} testID="taste-axes-note">
              {axisSaved ? t('tasteProfile.chosenSaved') : t('tasteProfile.chosenHint')}
            </Text>
          </View>
        )}

        {/* What the cook ruled out — the hard rules, editable here. */}
        <View style={styles.block} testID="taste-declared">
          <Text style={styles.blockTitle}>{t('tasteProfile.declaredTitle')}</Text>
          <View style={styles.card}>
            <Text style={styles.cardLabel}>{t('tasteProfile.allergiesLabel')}</Text>
            <View style={styles.chips}>
              {ALLERGEN_KEYS.map((key) => (
                <AllergenChip
                  key={key}
                  testID={`taste-allergen-${key}`}
                  label={t(ALLERGEN_LABEL[key])}
                  selected={allergens.includes(key)}
                  onPress={() => toggleAllergen(key)}
                />
              ))}
            </View>
            {customAllergens.length > 0 && (
              <Text style={styles.cardValue}>{customAllergens.join(' · ')}</Text>
            )}
          </View>
          <TouchableOpacity
            style={styles.linkRow}
            onPress={() => router.push('/preferences/diet')}
            testID="taste-diet-link"
          >
            <View style={styles.linkText}>
              <Text style={styles.cardLabel}>{t('tasteProfile.dietsLabel')}</Text>
              <Text style={styles.cardValue}>
                {dietaryRestrictions.length > 0
                  ? dietaryRestrictions.join(', ')
                  : t('tasteProfile.dietsNone')}
              </Text>
              <Text style={styles.cardValue}>
                {dislikedIngredients.length > 0
                  ? t('tasteProfile.excludedCount', { count: dislikedIngredients.length })
                  : t('tasteProfile.excludedNone')}
              </Text>
            </View>
            <Feather name="chevron-right" size={20} color={Paprika.textSecondary} />
          </TouchableOpacity>
          <Text style={styles.blockNote}>{t('tasteProfile.declaredNote')}</Text>
        </View>

        {/* What the digest learned — ranks, never excludes. */}
        <View style={styles.block} testID="taste-learned">
          <Text style={styles.blockTitle}>{t('tasteProfile.learnedTitle')}</Text>
        {isEmptyState ? (
          /* Empty state */
          <View style={styles.emptyState}>
            <Feather name="zap" size={48} color={Paprika.textMuted} />
            <Text style={styles.emptyTitle}>{t('tasteProfile.noProfile')}</Text>
            <Text style={styles.emptyText}>
              {t('tasteProfile.noProfileText')}
            </Text>
          </View>
        ) : isPendingDigest ? (
          /* Pending digest state */
          <View style={styles.emptyState}>
            <Feather name="loader" size={48} color={Paprika.accent} />
            <Text style={styles.emptyTitle}>{t('tasteProfile.buildingProfile')}</Text>
            <Text style={styles.emptyText}>
              {t('tasteProfile.buildingProfileText', { count: feedbackCount, plural: feedbackCount !== 1 ? 's' : '' })}
            </Text>
          </View>
        ) : (
          /* Profile content */
          <>
            {/* Preferences */}
            <Text style={styles.sectionTitle}>{t('tasteProfile.preferences')}</Text>
            {Object.entries(profile!.preferences).map(([key, value]) => (
              <View key={key} style={styles.preferenceCard}>
                <Text style={styles.preferenceLabel}>{formatPreferenceKey(key)}</Text>
                <Text style={styles.preferenceValue}>{value}</Text>
              </View>
            ))}

            {/* Recent Insights */}
            {profile!.recentInsights.length > 0 && (
              <>
                <Text style={[styles.sectionTitle, { marginTop: PaprikaSpacing.lg }]}>
                  {t('tasteProfile.recentInsights')}
                </Text>
                {profile!.recentInsights.map((insight, i) => (
                  <View key={i} style={styles.insightRow}>
                    <View style={styles.insightDateBadge}>
                      <Text style={styles.insightDateText}>{formatShortDate(insight.date, locale)}</Text>
                    </View>
                    <Text style={styles.insightText}>{insight.insight}</Text>
                  </View>
                ))}
              </>
            )}

            {/* Changelog */}
            {profile!.changeLog.length > 0 && (
              <>
                <Text style={[styles.sectionTitle, { marginTop: PaprikaSpacing.lg }]}>
                  {t('tasteProfile.howItsImproving')}
                </Text>
                {visibleChangelog.map((entry, i) => (
                  <View key={i} style={styles.changelogEntry}>
                    <View style={styles.changelogDot} />
                    <View style={styles.changelogContent}>
                      <Text style={styles.changelogVersion}>v{entry.version}</Text>
                      <Text style={styles.changelogSummary}>{entry.summary}</Text>
                      <Text style={styles.changelogDate}>{formatShortDate(entry.date, locale)}</Text>
                    </View>
                  </View>
                ))}
                {profile!.changeLog.length > 3 && (
                  <TouchableOpacity
                    onPress={() => setChangelogExpanded(!changelogExpanded)}
                    style={styles.expandButton}
                  >
                    <Text style={styles.expandButtonText}>
                      {changelogExpanded
                        ? t('tasteProfile.showLess')
                        : t('tasteProfile.showMore', { count: profile!.changeLog.length - 3 })}
                    </Text>
                    <Feather
                      name={changelogExpanded ? 'chevron-up' : 'chevron-down'}
                      size={16}
                      color={Paprika.accent}
                    />
                  </TouchableOpacity>
                )}
              </>
            )}
          </>
        )}
          <Text style={styles.blockNote}>{t('tasteProfile.learnedNote')}</Text>
        </View>
      </ScrollView>
    </View>
  );
};

// ---- Styles ----

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: Platform.OS === 'ios' ? 56 : Platform.OS === 'web' ? 16 : 40,
    paddingBottom: PaprikaSpacing.md,
    backgroundColor: Paprika.background,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  headerSubtitle: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.xxl,
    // One reading column on a wide screen: an axis row stretched across 1440px
    // put its two readings a hand apart and the comparison stopped being one.
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
  },

  // Empty state
  emptyState: {
    // The empty state used to own the whole screen; it is a block among three
    // now, so it keeps its centring and loses the full-page padding.
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: PaprikaSpacing.lg,
    paddingHorizontal: PaprikaSpacing.md,
  },
  emptyTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.md,
    textAlign: 'center',
  },
  emptyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.sm,
    textAlign: 'center',
    lineHeight: 22,
  },

  // Blocks — chosen / declared / learned
  block: {
    marginBottom: PaprikaSpacing.xl,
  },
  blockTitle: {
    ...PaprikaTypography.labelMedium,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: Paprika.accent,
    marginBottom: PaprikaSpacing.sm,
  },
  blockNote: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: PaprikaSpacing.sm,
    lineHeight: 18,
  },
  axisList: {
    gap: PaprikaSpacing.sm,
  },
  card: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)',
  },
  cardLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
    marginBottom: 6,
  },
  cardValue: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    marginTop: 4,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)',
  },
  linkText: {
    flex: 1,
  },

  // Sections
  sectionTitle: {
    ...PaprikaTypography.titleSmall,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
  },

  // Preference cards
  preferenceCard: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  preferenceLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
    marginBottom: 4,
  },
  preferenceValue: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    lineHeight: 22,
  },

  // Insights
  insightRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: PaprikaSpacing.sm,
  },
  insightDateBadge: {
    backgroundColor: Paprika.accentLight,
    borderRadius: PaprikaRadius.sm,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 4,
    marginRight: PaprikaSpacing.sm,
    minWidth: 70,
    alignItems: 'center',
  },
  insightDateText: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
    fontWeight: '600',
  },
  insightText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    flex: 1,
    lineHeight: 22,
  },

  // Changelog
  changelogEntry: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: PaprikaSpacing.sm,
  },
  changelogDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Paprika.accent,
    marginTop: 6,
    marginRight: PaprikaSpacing.sm,
  },
  changelogContent: {
    flex: 1,
  },
  changelogVersion: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
  },
  changelogSummary: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  changelogDate: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    marginTop: 2,
  },

  // Expand button
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: PaprikaSpacing.sm,
  },
  expandButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
    marginRight: 4,
  },
});

export default TasteProfileScreen;
